import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateEmailNotification } from "@/features/adminEmailNotifications/hooks/useCreateEmailNotification";

interface EmailNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailNotificationModal: React.FC<EmailNotificationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    form,
    templates,
    previewHtml,
    isLoading,
    error,
    createLoading,
    createError,
    fetchTemplates,
    handlePreview,
    submitForm,
    resetForm,
  } = useCreateEmailNotification(onClose);

  const { handleSubmit, control, watch } = form;
  const mailchimpTemplateId = watch("mailchimpTemplateId");

  // When modal opens, fetch the list of Mailchimp templates
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      resetForm();
    }
  }, [isOpen, fetchTemplates, resetForm]);

  // If modal is closed, don't render anything
  if (!isOpen) return null;

  return (
    // Fullscreen modal overlay
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Fullscreen modal container */}
      <div className="flex h-full w-full flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4 lg:p-6">
          <h2 className="text-xl font-bold">Notify Members</h2>
          <Button onClick={onClose} size="sm" variant="ghost">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Main Content: Two-column layout for Email Composer and Preview */}
        <Form {...form}>
          <form
            className="flex flex-col h-[calc(100%-70px)]"
            onSubmit={handleSubmit(submitForm)}
          >
            <div className="grid h-full grid-cols-1 md:grid-cols-2 overflow-hidden">
              {/* Email Composition Side - Scrollable */}
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex-grow overflow-y-auto p-4 lg:p-6">
                  <div className="space-y-4 flex flex-col h-full">
                    <FormField
                      control={control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem className="flex-shrink-0">
                          <FormLabel>Email Subject *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Enter email subject"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="mailchimpTemplateId"
                      render={({ field }) => (
                        <FormItem className="flex-shrink-0">
                          <FormLabel>Select Template *</FormLabel>
                          <FormControl>
                            <Select
                              disabled={isLoading || templates.length === 0}
                              onValueChange={(value) =>
                                field.onChange(Number(value))
                              }
                              value={field.value?.toString() || ""}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a template" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {templates.map((template) => (
                                    <SelectItem
                                      key={template.id}
                                      value={template.id.toString()}
                                    >
                                      {template.templateName}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="customContent"
                      render={({ field }) => (
                        <FormItem className="flex-grow flex flex-col">
                          <FormLabel>Email Content *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              className="flex-grow min-h-[300px] h-full resize-none"
                              placeholder="Enter your custom content here..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Display any local error or createError */}
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    {createError && (
                      <p className="text-sm text-red-500">{createError}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Side - Scrollable */}
              <div className="flex flex-col h-full overflow-hidden border-t md:border-t-0 md:border-l">
                <div className="border-b p-2 text-center font-medium">
                  Email Preview
                </div>
                <div className="flex-grow overflow-y-auto p-4">
                  {previewHtml ? (
                    <div
                      className="email-preview"
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-gray-400">
                      Select a template and click Preview to see how your email
                      will look.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sticky footer with action buttons */}
            <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t bg-white mt-auto">
              <Button
                disabled={isLoading || !mailchimpTemplateId}
                onClick={handlePreview}
                type="button"
                variant="outline"
              >
                {isLoading ? "Loading..." : "Preview Email"}
              </Button>
              <Button
                disabled={isLoading || createLoading}
                type="submit"
                variant="default"
              >
                {createLoading ? "Submitting..." : "Submit for Approval"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EmailNotificationModal;
